/**
 * iter-262 — useRemixPack
 *
 * Reads `?remix=<pack_id>` from the URL on /studio mount. If present:
 *   1. Fetches the pack via `getStemPack(packId)`
 *   2. Bumps the marketplace `remix_count` via `remixStemPack(packId)`
 *      (fire-and-forget — failing this shouldn't block the user)
 *   3. Loads each stem URL into the studio's next free channels using
 *      `engine.loadUrlToChannel`, routed through the iter-249 proxy so
 *      fal.media CORS / corporate firewalls don't bite.
 *   4. Jumps the mixer to the first loaded channel via the existing
 *      `elpm:jump-to-channel` window event.
 *   5. Clears `?remix=` from the URL so a refresh doesn't re-fire.
 *
 * Idempotent — fires once per pack_id per studio session. Safe to mount
 * before the engine is ready; the effect waits until `engine.channels`
 * is populated.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getStemPack, remixStemPack } from "@/lib/marketplaceApi";

const API = process.env.REACT_APP_BACKEND_URL;

export default function useRemixPack({
  engine,
  setSelectedChannel,
  setVisibleChannels,
  setLoadedCount,
  setMaxDur,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const firedRef = useRef(new Set()); // pack_ids we've already processed
  // Stash the engine in a ref so the effect can stay stable on
  // `location.search` only. `engine.channels` is unstable (array
  // reference re-creates as channels mutate), so depending on it makes
  // the effect re-fire mid-flight and cancel its own first closure.
  const engineRef = useRef(engine);
  engineRef.current = engine;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const packId = params.get("remix");
    if (!packId) return;
    if (firedRef.current.has(packId)) return;
    // Wait until the engine has its channel array — if not ready yet,
    // bail without registering as "fired" so a future location/engine
    // tick re-tries.
    if (!engineRef.current?.channels) return;
    firedRef.current.add(packId);

    // NOTE on StrictMode: in dev React invokes effects twice. The
    // first effect closure runs its async work, the cleanup runs, then
    // the second closure tries to run BUT is short-circuited by the
    // `firedRef.has(packId)` check above. There's no need for an
    // additional in-flight `cancelled` flag — `firedRef` already
    // guarantees the work runs exactly once per pack-id, and dropping
    // the cancellation lets the first closure run cleanly to
    // completion (including the counter bump + URL clean-up).
    (async () => {
      const toastId = toast.loading("Pulling stem pack into your studio…");
      try {
        const pack = await getStemPack(packId);
        const stems = pack.stems || [];
        if (stems.length === 0) {
          toast.dismiss(toastId);
          toast.error("This pack has no stems");
          return;
        }
        // Best-effort counter bump — failure should NOT block the load.
        remixStemPack(packId).catch(() => {});

        const engine = engineRef.current;
        // Find enough free channels.
        const targetIdxs = engine.freeChannelIndices
          ? engine.freeChannelIndices(stems.length)
          : [];
        if (!targetIdxs.length) {
          toast.dismiss(toastId);
          toast.error("No free channels in the mixer", {
            description: "Free up some channels (right-click → Clear) and try again.",
          });
          return;
        }

        const loaded = [];
        const failed = [];
        for (let i = 0; i < stems.length; i++) {
          const stem = stems[i];
          const targetIdx = targetIdxs[i];
          if (targetIdx == null) {
            failed.push({ name: stem.name, error: "no free channel" });
            continue;
          }
          // Route through the iter-249 proxy so fal.media CORS quirks
          // can never block the fetch (already battle-tested by the
          // useStemExtraction pipeline).
          const proxiedUrl = `${API}/api/stems/proxy?url=${encodeURIComponent(stem.url)}`;
          try {
            await engine.loadUrlToChannel(targetIdx, proxiedUrl, `${stem.name}.mp3`);
            loaded.push({ name: stem.name, channel: targetIdx });
          } catch (err) {
            // Fall back to direct fetch in case the proxy itself is
            // throttled / unavailable.
            try {
              await engine.loadUrlToChannel(targetIdx, stem.url, `${stem.name}.mp3`);
              loaded.push({ name: stem.name, channel: targetIdx });
            } catch (err2) {
              failed.push({ name: stem.name, error: err2.message || err.message });
            }
          }
        }

        toast.dismiss(toastId);

        if (loaded.length === 0) {
          toast.error("Could not load any stems", {
            description: failed.map((f) => `${f.name}: ${f.error}`).join(" · "),
          });
          return;
        }

        // Update engine-derived state.
        if (typeof setMaxDur === "function") setMaxDur(engine.maxDuration);
        if (typeof setLoadedCount === "function") setLoadedCount((c) => c + loaded.length);

        // Jump to the first loaded channel.
        const firstCh = loaded[0]?.channel;
        const lastCh = Math.max(...loaded.map((l) => l.channel));
        if (typeof setSelectedChannel === "function" && firstCh != null) {
          setSelectedChannel(firstCh);
        }
        if (typeof setVisibleChannels === "function") {
          setVisibleChannels((cur) => Math.max(cur || 8, lastCh + 1));
        }
        requestAnimationFrame(() => {
          try {
            window.dispatchEvent(new CustomEvent("elpm:jump-to-channel", { detail: { ch: firstCh } }));
          } catch (e) { void e; }
        });

        const padCh = (n) => String((n ?? 0) + 1).padStart(2, "0");
        toast.success(`Remixing "${pack.title}" · ${loaded.length} stems loaded`, {
          description:
            (failed.length ? `${failed.length} skipped · ` : "") +
            loaded.map((l) => `${l.name}→CH${padCh(l.channel)}`).join(" · "),
          duration: 10000,
        });
      } catch (e) {
        toast.dismiss(toastId);
        if (e.status === 404) {
          toast.error("Pack not found", {
            description: "It may have been deleted by the publisher.",
          });
        } else {
          toast.error("Could not load pack", { description: e.message });
        }
      } finally {
        // Make sure the loading toast is always dismissed — even when
        // an early-return path bailed before the explicit dismiss.
        toast.dismiss(toastId);
        // Clear ?remix= from the URL so a refresh doesn't re-fire. We
        // always clean up, even if the load was cancelled by a re-fire,
        // so a stale ?remix= doesn't leak after a failed run.
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete("remix");
        const nextSearch = nextParams.toString();
        navigate(
          { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
          { replace: true },
        );
      }
    })();
    // Intentionally only watching the URL — engine + navigate are read via
    // refs/stable identities, and re-firing on them would replay the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
}
